// src/services/deductionService.js
import { supabase } from './supabase';
import moment from 'moment';

export const deductionService = {
  /**
   * Get all deduction types for a company
   */
  async getDeductionTypes(company_id) {
    try {
      const { data, error } = await supabase
        .from('deduction_types')
        .select('*')
        .eq('company_id', company_id)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching deduction types:', error);
      throw error;
    }
  },

  /**
   * Create a new deduction type
   */
  async createDeductionType(deductionType) {
    try {
      const { data, error } = await supabase
        .from('deduction_types')
        .insert([{
          ...deductionType,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating deduction type:', error);
      throw error;
    }
  },

  /**
   * Update a deduction type
   */
  async updateDeductionType(id, updates) {
    try {
      const { data, error } = await supabase
        .from('deduction_types')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating deduction type:', error);
      throw error;
    }
  },

  /**
   * Delete a deduction type
   */
  async deleteDeductionType(id) {
    try {
      const { error } = await supabase
        .from('deduction_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting deduction type:', error);
      throw error;
    }
  },

  /**
   * Get employee deductions
   */
  async getEmployeeDeductions(employee_id, company_id) {
    try {
      const { data, error } = await supabase
        .from('employee_deductions')
        .select(`
          *,
          deduction_types (
            id,
            name,
            description,
            is_percentage
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching employee deductions:', error);
      throw error;
    }
  },

  /**
   * Get active employee deductions for a date range
   */
  async getActiveEmployeeDeductions(employee_id, company_id, startDate, endDate) {
    try {
      const { data, error } = await supabase
        .from('employee_deductions')
        .select(`
          *,
          deduction_types (
            id,
            name,
            description,
            is_percentage
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id)
        .or(`start_date.lte.${endDate},end_date.gte.${startDate}`)
        .order('start_date');

      if (error) throw error;

      const activeDeductions = (data || []).filter(deduction => {
        const deductionStart = moment(deduction.start_date);
        const deductionEnd = moment(deduction.end_date);
        const periodStart = moment(startDate);
        const periodEnd = moment(endDate);

        return deductionStart.isSameOrBefore(periodEnd) && deductionEnd.isSameOrAfter(periodStart);
      });

      return activeDeductions;
    } catch (error) {
      console.error('Error fetching active employee deductions:', error);
      throw error;
    }
  },

  /**
   * Create employee deduction
   */
  async createEmployeeDeduction(deduction) {
    try {
      const { data, error } = await supabase
        .from('employee_deductions')
        .insert([{
          ...deduction,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating employee deduction:', error);
      throw error;
    }
  },

  /**
   * Update employee deduction
   */
  async updateEmployeeDeduction(id, updates) {
    try {
      const { data, error } = await supabase
        .from('employee_deductions')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating employee deduction:', error);
      throw error;
    }
  },

  /**
   * Delete employee deduction
   */
  async deleteEmployeeDeduction(id) {
    try {
      const { error } = await supabase
        .from('employee_deductions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting employee deduction:', error);
      throw error;
    }
  },

  /**
   * Calculate deduction amount
   * IMPORTANT: Percentage deductions are calculated on BASE GROSS PAY (before allowances)
   * @param {Object} deduction - Deduction record
   * @param {number} baseGrossPay - Base gross pay BEFORE allowances
   */
  calculateDeductionAmount(deduction, baseGrossPay) {
    if (deduction.deduction_types?.is_percentage) {
      // Percentage of BASE gross pay
      return baseGrossPay * (deduction.amount / 100);
    }
    // Fixed amount
    return deduction.amount;
  },

  /**
   * Get deduction summary for payroll period
   * @param {string} employee_id - Employee ID
   * @param {string} company_id - Company ID
   * @param {string} startDate - Period start date
   * @param {string} endDate - Period end date
   * @param {number} baseGrossPay - Base gross pay BEFORE allowances
   */
  async getDeductionSummary(employee_id, company_id, startDate, endDate, baseGrossPay) {
    try {
      const deductions = await this.getActiveEmployeeDeductions(
        employee_id, 
        company_id, 
        startDate, 
        endDate
      );

      let totalDeductions = 0;
      const deductionBreakdown = [];

      deductions.forEach(deduction => {
        const amount = this.calculateDeductionAmount(deduction, baseGrossPay);
        
        totalDeductions += amount;

        deductionBreakdown.push({
          id: deduction.id,
          name: deduction.deduction_types?.name || 'Unknown',
          amount: amount,
          is_percentage: deduction.deduction_types?.is_percentage || false,
          original_amount: deduction.amount,
          start_date: deduction.start_date,
          end_date: deduction.end_date
        });
      });

      return {
        totalDeductions,
        deductionBreakdown,
        deductionCount: deductions.length
      };
    } catch (error) {
      console.error('Error calculating deduction summary:', error);
      throw error;
    }
  },

  /**
   * Create default Mauritius deduction types
   */
  async createDefaultDeductionTypes(company_id) {
    const defaultTypes = [
      {
        company_id,
        name: 'CSG',
        description: 'Contribution Sociale Généralisée (4% of base gross pay)',
        is_percentage: true,
        amount: 4.00
      },
      {
        company_id,
        name: 'Levy',
        description: 'National Savings Fund Levy (1.5% of base gross pay)',
        is_percentage: true,
        amount: 1.50
      },
      {
        company_id,
        name: 'NPF',
        description: 'National Pension Fund (2.5% of base gross pay)',
        is_percentage: true,
        amount: 2.50
      },
      {
        company_id,
        name: 'Advance Payment',
        description: 'Salary advance payment',
        is_percentage: false,
        amount: 0
      },
      {
        company_id,
        name: 'Loan Repayment',
        description: 'Employee loan repayment',
        is_percentage: false,
        amount: 0
      }
    ];

    try {
      const { data, error } = await supabase
        .from('deduction_types')
        .insert(defaultTypes.map(type => ({
          ...type,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })))
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating default deduction types:', error);
      throw error;
    }
  }
};

export default deductionService;